import type { ReactNode } from 'react'
import type { StockRow } from '@/lib/market/types'

import { useMemo } from 'react'
import { ChangeText } from '@/components/market/change'
import { useQuotes } from '@/components/market/live-provider'
import { formatCompact, formatKes, formatNumber } from '@/lib/market/format'
import StatTile from '@/components/market/stat-tile'

interface MarketPulseProps {
    stocks: StockRow[]
    capTrend: number[]
    turnoverTrend: number[]
}

export const useMarketTotals = (stocks: StockRow[]) => {
    const specs = useMemo(() => stocks.map(stock => stock.live), [stocks])
    const quotes = useQuotes(specs)

    return useMemo(() => {
        let cap = 0
        let previousCap = 0
        let turnover = 0
        let volume = 0
        let deals = 0
        let advancers = 0
        let decliners = 0
        let unchanged = 0

        stocks.forEach((stock, index) => {
            const quote = quotes[index]
            cap += quote.last * stock.shares
            previousCap += quote.prevClose * stock.shares
            turnover += quote.turnover
            volume += quote.volume
            deals += quote.deals

            if (quote.change > 0) advancers++
            else if (quote.change < 0) decliners++
            else unchanged++
        })

        return {
            quotes,
            cap,
            capChange: previousCap > 0 ? (cap / previousCap - 1) * 100 : 0,
            turnover,
            volume,
            deals,
            advancers,
            decliners,
            unchanged
        }
    }, [stocks, quotes])
}

export const BreadthBar = ({ advancers, decliners, unchanged }: { advancers: number, decliners: number, unchanged: number }): ReactNode => {
    const total = Math.max(1, advancers + decliners + unchanged)
    const segments = [
        { key: 'up', value: advancers, color: 'var(--up)', label: 'Advancing' },
        { key: 'flat', value: unchanged, color: 'var(--flat)', label: 'Unchanged' },
        { key: 'down', value: decliners, color: 'var(--down)', label: 'Declining' }
    ]

    return (
        <div>
            <div className='flex h-2.5 w-full gap-0.5 overflow-hidden rounded-full' role='img' aria-label={`${advancers} advancing, ${unchanged} unchanged, ${decliners} declining`}>
                {segments.filter(segment => segment.value > 0).map(segment => (
                    <span key={segment.key} className='h-full first:rounded-l-full last:rounded-r-full' style={{ width: `${(segment.value / total) * 100}%`, background: segment.color }} />
                ))}
            </div>

            <dl className='mt-2.5 grid grid-cols-3 gap-2 text-xs'>
                {segments.map(segment => (
                    <div key={segment.key}>
                        <dt className='flex items-center gap-1.5 text-muted-foreground'>
                            <span aria-hidden='true' className='inline-block size-2 rounded-[3px]' style={{ background: segment.color }} />
                            {segment.label}
                        </dt>
                        <dd className='mt-0.5 text-base font-semibold text-foreground'>{segment.value}</dd>
                    </div>
                ))}
            </dl>
        </div>
    )
}

const MarketPulse = ({ stocks, capTrend, turnoverTrend }: MarketPulseProps): ReactNode => {
    const totals = useMarketTotals(stocks)
    const capSeries = useMemo(() => [...capTrend.slice(0, -1), totals.cap], [capTrend, totals.cap])
    const turnoverSeries = useMemo(() => [...turnoverTrend.slice(0, -1), totals.turnover], [turnoverTrend, totals.turnover])

    return (
        <div className='grid grid-cols-2 gap-3 lg:grid-cols-4'>
            <StatTile
                label='Market capitalisation'
                value={formatKes(totals.cap)}
                delta={<ChangeText value={totals.capChange} showIcon />}
                trend={capSeries}
            />
            <StatTile
                label='Equity turnover today'
                value={formatKes(totals.turnover)}
                hint='Value of shares traded'
                trend={turnoverSeries}
            />
            <StatTile
                label='Shares traded'
                value={formatCompact(totals.volume)}
                hint={`${formatNumber(totals.deals)} deals`}
            />
            <StatTile
                label='Market breadth'
                value={`${totals.advancers} up, ${totals.decliners} down`}
                hint={`${totals.unchanged} unchanged of ${stocks.length} listed`}
            />
        </div>
    )
}

export default MarketPulse
